<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  canUnregister,
  registerHandler,
  unregisterHandler,
  type ProtocolScheme,
  type RegisterResult,
} from '@/protocols/register'

const props = defineProps<{
  scheme: ProtocolScheme
  title: string
  description: string
  example: string
}>()

const { t } = useI18n()

// The browser does not tell whether a handler is registered, so this is only what the last tap did.
const outcome = ref<'registered' | 'unregistered' | Exclude<RegisterResult, 'ok'> | null>(null)

function run(action: typeof registerHandler, done: 'registered' | 'unregistered'): void {
  const result = action(props.scheme)
  outcome.value = result === 'ok' ? done : result
}
</script>

<template>
  <div class="handler">
    <div class="row">
      <span class="name">{{ title }}</span>
      <button type="button" class="ui-btn" @click="run(registerHandler, 'registered')">
        {{ t('settings.protocols.register') }}
      </button>
      <button
        v-if="canUnregister()"
        type="button"
        class="ui-btn"
        @click="run(unregisterHandler, 'unregistered')"
      >
        {{ t('settings.protocols.unregister') }}
      </button>
    </div>
    <p class="hint">{{ description }}</p>
    <p class="hint">
      <code>{{ example }}</code>
    </p>
    <p
      v-if="outcome"
      class="hint"
      :class="{ error: outcome === 'blocked' || outcome === 'error' }"
      role="status"
    >
      {{ t(`settings.protocols.${outcome}`) }}
    </p>
  </div>
</template>

<style scoped>
.row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.name {
  flex: 1;
  font-weight: 600;
}

.hint {
  margin: 0.25rem 0 0;
  font-size: 0.85rem;
  color: var(--tg-hint-color);
}

.error {
  color: var(--tg-destructive-text-color);
}
</style>
